import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import type { FieldConfig, FieldInputProps } from "formik";
import type { Collection } from "../../types/form.types";
import formStyles from "../../features/MainForm/Form.module.css";
import styles from "./Select.module.css";
import "../../styles/animations.css";

export interface SelectFieldProps {
  label: string;
  id: string;
  name: string;
  getFieldProps: (
    nameOrOptions: string | FieldConfig<any>
  ) => FieldInputProps<any>;
  disabled?: boolean;
  touched?: { [key: string]: boolean | boolean[] };
  errors?: { [key: string]: string | string[] };
  options: Collection[];
  variant?: "default" | "outlined" | "filled";
}

export const SelectField: React.FC<SelectFieldProps> = ({
  label,
  id,
  name,
  getFieldProps,
  disabled,
  touched,
  errors,
  options,
  variant = "default",
}) => {
  const { t } = useTranslation();
  const selectClass = classNames(styles.selectField, {
    [styles.selectFieldOutlined]: variant === "outlined",
    [styles.selectFieldFilled]: variant === "filled",
    [styles.selectFieldError]: touched?.[name] && errors?.[name],
  });

  return (
    <div className={classNames(formStyles.formGroup, "fadeIn")}>
      <label
        htmlFor={id}
        className={classNames({
          [formStyles.labelGroup]: !disabled,
          [formStyles.labelGroupDisabled]: disabled,
        })}
      >
        {label}
      </label>
      <select
        id={id}
        {...getFieldProps(name)}
        className={selectClass}
        disabled={disabled}
      >
        <option value="" disabled>
          {t("field.select.placeholder")}
        </option>
        {options.map((option) => (
          <option key={option.id} value={option.code}>
            {option.value}
          </option>
        ))}
      </select>
      {touched?.[name] && errors?.[name] && (
        <p className={formStyles.formError}>{errors[name]}</p>
      )}
    </div>
  );
};
